/* ===================================================================
   THE HERO'S LABELS GET THE SAME CYLINDER THE CONFIGURATOR'S DO.

   `sections/hero-scene.liquid` renders its labels flat inside a bowed paper: the
   outline is `--sdw-label-bow-mask`, the printing is not warped, and `D-187` argued
   that was correct at this size on `D-123`'s own measurement — the bow is worth about
   1.6 CSS pixels on a 182px label.

   `D-188` reversed it on the client's direction: the hero's bottle and the
   configurator's bottle must wear the same label, and "the same" means the same
   picture and not a picture that is within two pixels of it. This file is what makes
   that true, and **it is thirty lines rather than a second implementation** because
   the curve lives in `assets/label-warp.js` and both surfaces call it.

   **What it does.** For each label in the stack it bakes the flat artwork into a
   512×768 warped bitmap and swaps that in as the image's own source. The box does not
   move: the warp is a pure vertical remapping in fractions of the picture's height, and
   `.soul-scene__label-art` is `object-fit: fill` in a box whose size comes from the
   bottle, so the warped bitmap lands in exactly the box the flat one was in. That is the
   same arrangement `assets/generator.css` has — `background-size: 100% 100%` over the
   same fallback chain.

   **`srcset` has to go with the swap.** A responsive set outranks `src`, so an image
   left carrying one would go on painting the flat picture the browser had already
   chosen. It is removed in the same statement, which is safe because the baked bitmap is
   a fixed 512 wide — about four times the label's own rendered width at 1440.

   **And so does `loading="lazy"`, and that one is not tidiness — it is a defect this
   file had.** Measured in Chrome against the dev server: labels 2 and 3 took their blob
   `src` and then sat at `complete: false`, `naturalWidth: 0`, for as long as the page was
   open. They are in the viewport from the first frame — stacked on the bottle at
   `opacity: 0` — so the deferral had already been resolved once, and assigning a new
   `src` to an image still carrying the attribute did not schedule a second load. The
   labels behind the first would simply never have appeared. A blob is in memory already,
   so there is no fetch left to defer and nothing is given up by dropping it.

   **The source that gets baked is not the one on screen.** `data-sdw-warp-src` carries a
   512-wide copy chosen by Liquid, so the bake is never run over whichever small
   candidate the browser happened to pick out of the srcset.

   **The first label bakes now and the rest wait for an idle slot**, which is where the
   `loading="lazy"` on labels 2 and 3 went. That attribute stopped meaning anything the
   moment this file existed: it defers the browser's *own* fetch, and this fetches the
   artwork itself. Three fetches and three canvas passes at boot would land on top of the
   hero's first paint for no gain, since nothing behind label one is wanted for 4.9
   seconds. `requestIdleCallback` with a timeout is the honest version of what the
   attribute used to buy, and the timeout is what stops a busy main thread from holding
   the loop back past its first turn.

   **Nothing here is required for the hero to be correct.** With this file absent,
   blocked, or in a browser with no `createImageBitmap`, every label keeps its flat
   picture inside the bowed paper — which is exactly what shipped under `D-187` and is a
   composition rather than a broken one. That is also why it makes no attempt to retry:
   the failure mode is the previous build.
   =================================================================== */

(() => {
  if (window.__soulSceneLabelsBooted) return;
  window.__soulSceneLabelsBooted = true;

  const warp = window.SDW_LABEL_WARP;
  if (!warp) return;

  const labels = Array.from(
    document.querySelectorAll('.soul-scene__label-art[data-sdw-warp-src]')
  );
  if (labels.length === 0) return;

  const bake = (img) => {
    const src = img.dataset.sdwWarpSrc;
    if (!src) return;

    warp.toUrl(src).then((url) => {
      if (!url) return;
      /* Order matters by one frame: clearing the set first means the assignment to
         `src` is the only candidate there is, so the browser cannot repaint the flat
         picture between the two statements. `loading` goes with them — see the note
         above for the defect that taught this. */
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('loading');
      img.removeAttribute('fetchpriority');
      img.src = url;
      img.dataset.sdwWarped = 'true';
    });
  };

  const [first, ...rest] = labels;
  bake(first);

  if (rest.length === 0) return;

  const later = () => rest.forEach(bake);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(later, { timeout: 2000 });
  } else {
    window.setTimeout(later, 600);
  }
})();
